import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast, Toaster } from 'sonner';
import { 
  Calendar, Clock, User, Mail, Phone, Building2, 
  CheckCircle, XCircle, AlertCircle, RefreshCcw,
  ChevronLeft, ChevronRight, Settings, Ban
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminBookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [updatingAvailability, setUpdatingAvailability] = useState(false);

  // Convert 24-hour time to 12-hour format
  const formatTime12hr = (time24) => {
    if (!time24) return time24;
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      const [bookingsRes, availabilityRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/bookings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/booking/availability`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (bookingsRes.ok) {
        const data = await bookingsRes.json();
        setBookings(data);
      }

      if (availabilityRes.ok) {
        const data = await availabilityRes.json();
        setAvailability(data);
      }
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/bookings/${bookingId}?status=${status}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(`Booking ${status}`);
        loadData();
      } else {
        throw new Error('Failed to update');
      }
    } catch (e) {
      toast.error('Failed to update booking');
    }
  };

  const updateAvailability = async () => {
    setUpdatingAvailability(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/booking/availability`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(availability)
      });

      if (res.ok) {
        toast.success('Availability updated');
      } else {
        throw new Error('Failed to update');
      }
    } catch (e) {
      toast.error('Failed to update availability');
    } finally {
      setUpdatingAvailability(false);
    }
  };

  const handleAvailabilityChange = (dayIndex, field, value) => {
    setAvailability(prev => prev.map((day, i) => 
      i === dayIndex ? { ...day, [field]: value } : day
    ));
  };

  const getStatusBadge = (status) => {
    const badges = {
      confirmed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      completed: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertCircle },
      no_show: { bg: 'bg-gray-100', text: 'text-gray-700', icon: Ban }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon size={12} className="mr-1" />
        {status}
      </span>
    );
  };

  // Filter bookings by month
  const filteredBookings = bookings.filter(b => {
    const bookingDate = new Date(b.date);
    return bookingDate.getMonth() === selectedMonth.getMonth() &&
           bookingDate.getFullYear() === selectedMonth.getFullYear();
  });

  // Group by date
  const groupedBookings = filteredBookings.reduce((acc, booking) => {
    if (!acc[booking.date]) acc[booking.date] = [];
    acc[booking.date].push(booking);
    return acc;
  }, {});

  // Stats
  const stats = {
    total: bookings.length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCcw className="animate-spin text-tech-blue" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Booking Calendar</h1>
          <p className="text-text-secondary mt-1">Manage appointments and availability</p>
        </div>
        <Button onClick={loadData} variant="outline">
          <RefreshCcw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-border-line">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-text-primary">{stats.total}</div>
            <div className="text-sm text-text-secondary">Total Bookings</div>
          </CardContent>
        </Card>
        <Card className="border-border-line">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.confirmed}</div>
            <div className="text-sm text-text-secondary">Confirmed</div>
          </CardContent>
        </Card>
        <Card className="border-border-line">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.completed}</div>
            <div className="text-sm text-text-secondary">Completed</div>
          </CardContent>
        </Card>
        <Card className="border-border-line">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-red-600">{stats.cancelled}</div>
            <div className="text-sm text-text-secondary">Cancelled</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-border-line">
        <button
          onClick={() => setActiveTab('bookings')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'bookings' 
              ? 'border-tech-blue text-tech-blue' 
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          <Calendar size={16} className="inline mr-2" />
          Bookings
        </button>
        <button
          onClick={() => setActiveTab('availability')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'availability' 
              ? 'border-tech-blue text-tech-blue' 
              : 'border-transparent text-text-secondary hover:text-text-primary'
          }`}
        >
          <Settings size={16} className="inline mr-2" />
          Availability Settings
        </button>
      </div>

      {/* Bookings Tab */}
      {activeTab === 'bookings' && (
        <>
          {/* Month Navigation */}
          <div className="flex items-center justify-between">
            <button 
              onClick={() => setSelectedMonth(new Date(selectedMonth.setMonth(selectedMonth.getMonth() - 1)))}
              className="p-2 hover:bg-page-bg rounded-lg"
            >
              <ChevronLeft size={20} />
            </button>
            <h2 className="text-lg font-semibold text-text-primary">
              {selectedMonth.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
            </h2>
            <button 
              onClick={() => setSelectedMonth(new Date(selectedMonth.setMonth(selectedMonth.getMonth() + 1)))}
              className="p-2 hover:bg-page-bg rounded-lg"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Bookings List */}
          {Object.keys(groupedBookings).length === 0 ? (
            <Card className="border-border-line">
              <CardContent className="p-12 text-center">
                <Calendar className="mx-auto mb-4 text-text-secondary" size={48} />
                <p className="text-text-secondary">No bookings for this month</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedBookings)
                .sort(([a], [b]) => new Date(a) - new Date(b))
                .map(([date, dateBookings]) => (
                <div key={date}>
                  <h3 className="font-semibold text-text-primary mb-3 flex items-center">
                    <Calendar size={16} className="mr-2 text-tech-blue" />
                    {new Date(date).toLocaleDateString('en-AU', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long' 
                    })}
                    <span className="ml-2 text-sm font-normal text-text-secondary">
                      ({dateBookings.length} booking{dateBookings.length > 1 ? 's' : ''})
                    </span>
                  </h3>
                  <div className="space-y-3">
                    {dateBookings.sort((a, b) => a.time.localeCompare(b.time)).map(booking => (
                      <Card key={booking.id} className="border-border-line">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-4">
                              <div className="bg-tech-blue/10 rounded-lg px-3 py-2 text-center">
                                <Clock className="mx-auto text-tech-blue mb-1" size={16} />
                                <div className="font-semibold text-tech-blue">{formatTime12hr(booking.time)}</div>
                                <div className="text-xs text-text-secondary">{booking.duration}min</div>
                              </div>
                              <div>
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="font-semibold text-text-primary">{booking.name}</span>
                                  {getStatusBadge(booking.status)}
                                </div>
                                <div className="text-sm text-text-secondary space-y-1">
                                  <div className="flex items-center">
                                    <Mail size={12} className="mr-2" />
                                    {booking.email}
                                  </div>
                                  {booking.phone && (
                                    <div className="flex items-center">
                                      <Phone size={12} className="mr-2" />
                                      {booking.phone}
                                    </div>
                                  )}
                                  {booking.company && (
                                    <div className="flex items-center">
                                      <Building2 size={12} className="mr-2" />
                                      {booking.company}
                                    </div>
                                  )}
                                </div>
                                {booking.message && (
                                  <p className="text-sm text-text-secondary mt-2 bg-page-bg p-2 rounded">
                                    "{booking.message}"
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              {booking.status === 'confirmed' && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => updateBookingStatus(booking.id, 'completed')}
                                    className="text-green-600 hover:bg-green-50"
                                  >
                                    <CheckCircle size={14} className="mr-1" />
                                    Complete
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                    className="text-red-600 hover:bg-red-50"
                                  >
                                    <XCircle size={14} className="mr-1" />
                                    Cancel
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Availability Tab */}
      {activeTab === 'availability' && (
        <Card className="border-border-line">
          <CardHeader>
            <CardTitle>Weekly Availability</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {availability.map((day, index) => (
                <div key={day.day_of_week} className="flex items-center space-x-4 p-4 bg-page-bg rounded-lg">
                  <div className="w-32">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={day.is_available}
                        onChange={(e) => handleAvailabilityChange(index, 'is_available', e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="font-medium text-text-primary">{day.day_name}</span>
                    </label>
                  </div>
                  
                  {day.is_available && (
                    <>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-text-secondary">From:</label>
                        <input
                          type="time"
                          value={day.start_time}
                          onChange={(e) => handleAvailabilityChange(index, 'start_time', e.target.value)}
                          className="px-3 py-1 rounded border border-border-line bg-surface"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-text-secondary">To:</label>
                        <input
                          type="time"
                          value={day.end_time}
                          onChange={(e) => handleAvailabilityChange(index, 'end_time', e.target.value)}
                          className="px-3 py-1 rounded border border-border-line bg-surface"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-text-secondary">Slot:</label>
                        <select
                          value={day.slot_duration}
                          onChange={(e) => handleAvailabilityChange(index, 'slot_duration', parseInt(e.target.value))}
                          className="px-3 py-1 rounded border border-border-line bg-surface"
                        >
                          <option value={15}>15 min</option>
                          <option value={30}>30 min</option>
                          <option value={45}>45 min</option>
                          <option value={60}>60 min</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
            
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={updateAvailability}
                disabled={updatingAvailability}
                className="bg-tech-blue hover:bg-tech-blue/90"
              >
                {updatingAvailability ? 'Saving...' : 'Save Availability'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminBookingsPage;
